import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Rating,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as RequestEditIcon,
  Refresh as RefreshIcon,
  Reply as ReplyIcon,
  HourglassEmpty as PendingIcon,
  Block as BlockIcon,
  WarningAmber as WarningIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'warning', icon: PendingIcon },
  changes_requested: { label: 'Modifs demandées', color: 'info', icon: WarningIcon },
  approved: { label: 'Approuvée', color: 'success', icon: ApproveIcon },
  rejected: { label: 'Rejetée', color: 'error', icon: BlockIcon }
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente' },
  { value: 'changes_requested', label: 'Modifs demandées' },
  { value: 'approved', label: 'Approuvées' },
  { value: 'rejected', label: 'Rejetées' },
  { value: 'reported', label: '🚩 Signalées' },
  { value: 'all', label: 'Toutes' }
];

const ACTION_LABELS = {
  approve: 'Approuver',
  reject: 'Rejeter',
  request_edit: 'Demander des modifications'
};

const ResponseModerationPage = () => {
  const { t } = useTranslation();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [moderationDialogOpen, setModerationDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [moderationAction, setModerationAction] = useState('');
  const [moderationNotes, setModerationNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // États pour la traduction automatique
  const [translating, setTranslating] = useState(false);
  const [translatedNotes, setTranslatedNotes] = useState('');
  const [translationResult, setTranslationResult] = useState(null);
  const [showTranslation, setShowTranslation] = useState(false);

  const fetchResponses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/reviews/admin/responses/pending', {
        params: {
          page,
          limit: 20,
          status: statusFilter === 'all' ? undefined : statusFilter,
          _t: Date.now()
        }
      });

      if (response.data && response.data.data) {
        setResponses(response.data.data.responses || []);
        setTotalPages(response.data.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching responses:', err);
      setError('Erreur lors du chargement des réponses');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const openModerationDialog = (response, action) => {
    setSelectedResponse(response);
    setModerationAction(action);
    setModerationNotes('');
    setTranslatedNotes('');
    setTranslationResult(null);
    setShowTranslation(false);
    setModerationDialogOpen(true);
  };

  const closeModerationDialog = () => {
    setModerationDialogOpen(false);
    setSelectedResponse(null);
    setModerationAction('');
    setModerationNotes('');
    setTranslatedNotes('');
    setTranslationResult(null);
    setShowTranslation(false);
  };

  const handleModerationChange = (event) => {
    setModerationNotes(event.target.value);
    // Réinitialiser la traduction si le texte change
    if (translationResult) {
      setTranslationResult(null);
      setShowTranslation(false);
    }
  };

  const handleTranslateNotes = async () => {
    if (!moderationNotes || moderationNotes.trim().length < 10) {
      setError('Les notes doivent contenir au moins 10 caractères pour la traduction');
      return;
    }

    setTranslating(true);
    setError(null);

    try {
      const response = await api.post(
        `/reviews/admin/responses/${selectedResponse.id}/translate-moderation`,
        { notes: moderationNotes.trim() }
      );

      if (response.data.success) {
        setTranslationResult(response.data.translation);
        setTranslatedNotes(response.data.translation.translatedNotes);
        setShowTranslation(true);
      }
    } catch (err) {
      console.error('Translation error:', err);
      const errorMsg = err.response?.data?.message || 'Erreur lors de la traduction';
      setError(errorMsg);
      
      if (err.response?.data?.translation) {
        setTranslationResult(err.response.data.translation);
      }
    } finally {
      setTranslating(false);
    }
  };

  const handleUseTranslation = () => {
    if (translatedNotes) {
      setModerationNotes(translatedNotes);
      setShowTranslation(false);
      setTranslationResult(null);
    }
  };

  const handleModerationSubmit = async () => {
    if (!selectedResponse || !moderationAction) return;

    if (!moderationNotes.trim() && (moderationAction === 'reject' || moderationAction === 'request_edit')) {
      setError('Les notes de modération sont requises pour cette action');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const responseId = selectedResponse.id;
      const actionDone = moderationAction;

      await api.patch(`/reviews/admin/responses/${responseId}/moderate`, {
        action: actionDone,
        notes: moderationNotes.trim() || undefined
      });

      setSuccessMessage('Réponse modérée avec succès');
      closeModerationDialog();
      // Mise à jour optimiste immédiate → préserve le focus / scroll
      const statusMap = { approve: 'approved', reject: 'rejected', request_edit: 'changes_requested' };
      const newStatus = statusMap[actionDone];
      if (newStatus) {
        setResponses(prev => prev.map(r =>
          r.id === responseId ? { ...r, response_moderation_status: newStatus } : r
        ));
      }
      // Rechargement en arrière-plan
      fetchResponses();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error moderating response:', err);
      setError(err.response?.data?.message || 'Erreur lors de la modération');
    } finally {
      setSubmitting(false);
    }
  };

  const StatusChip = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;

    return (
      <Chip
        icon={<Icon />}
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  const ResponseCard = ({ response }) => {
    const reviewerName = response.reviewer?.name || response.reviewer?.email || 'Utilisateur';
    const owner = response.reservation?.listing?.owner;
    const ownerName = owner?.name || owner?.email || 'Propriétaire';
    const equipmentName = response.reservation?.listing?.title || 'Équipement';
    const ratingValue = response.overall_rating || response.rating || 0;
    const commentText = response.overall_comment || response.comment;
    const reportInfo = response.responseReportInfo;

    return (
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Header */}
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <ReplyIcon color="primary" />
                <Typography variant="h6">Réponse à un avis</Typography>
                {reportInfo && (
                  <Chip
                    label={`🚩 ${reportInfo.report_count} signalement${reportInfo.report_count > 1 ? 's' : ''}`}
                    color="error"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Stack>
              <StatusChip status={response.response_moderation_status} />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Alert de signalement si présent */}
          {reportInfo && (
            <>
              <Grid item xs={12}>
                <Alert severity="error" icon={<WarningIcon />}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    ⚠️ Cette réponse a été signalée {reportInfo.report_count} fois
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Dernier signalement: {reportInfo.last_reported_at && format(new Date(reportInfo.last_reported_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </Typography>
                  
                  {/* Liste de tous les signalements */}
                  {reportInfo.details && reportInfo.details.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" fontWeight="600" display="block" gutterBottom>
                        Tous les signalements ({reportInfo.details.length}):
                      </Typography>
                      <Stack spacing={1} mt={1}>
                        {reportInfo.details.map((detail, index) => (
                          <Box 
                            key={detail.id || index} 
                            sx={{ 
                              bgcolor: 'error.50', 
                              p: 1.5, 
                              borderRadius: 1,
                              borderLeft: '3px solid',
                              borderColor: 'error.main'
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="start" spacing={1}>
                              <Box flex={1}>
                                <Typography variant="body2" fontWeight="500">
                                  {detail.reason}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                  Par: {detail.reporter?.name || detail.reporter?.email || 'Utilisateur'} • 
                                  Le {format(new Date(detail.reported_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                                </Typography>
                              </Box>
                              <Chip 
                                label={`#${index + 1}`} 
                                size="small" 
                                color="error" 
                                variant="outlined"
                              />
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Alert>
              </Grid>
            </>
          )}

          {/* Review Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Avis original
            </Typography>
            <Stack spacing={1} mt={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight="500">Note:</Typography>
                <Rating value={ratingValue} readOnly size="small" />
                <Typography variant="body2">({ratingValue}/5)</Typography>
              </Stack>
              <Typography variant="body2">
                <strong>De:</strong> {reviewerName}
              </Typography>
              <Typography variant="body2">
                <strong>Équipement:</strong> {equipmentName}
              </Typography>
              {commentText && (
                <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {commentText}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* Response */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Réponse du propriétaire
            </Typography>
            <Stack spacing={1} mt={1}>
              <Typography variant="body2">
                <strong>Par:</strong> {ownerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {response.owner_response_date && format(new Date(response.owner_response_date), 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </Typography>
              <Box sx={{ bgcolor: 'primary.50', p: 2, borderRadius: 1, mt: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {response.owner_response}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Moderation info if exists */}
          {response.response_moderation_notes && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="subtitle2" gutterBottom>
                    Notes de modération
                  </Typography>
                  <Typography variant="body2">
                    {response.response_moderation_notes}
                  </Typography>
                  {response.response_moderated_at && (
                    <Typography variant="caption" display="block" mt={1}>
                      Modéré le {format(new Date(response.response_moderated_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            </>
          )}

          {/* Actions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                startIcon={<ApproveIcon />}
                variant="contained"
                color="success"
                size="small"
                onClick={() => openModerationDialog(response, 'approve')}
                disabled={response.response_moderation_status === 'approved'}
              >
                Approuver
              </Button>
              <Button
                startIcon={<RequestEditIcon />}
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => openModerationDialog(response, 'request_edit')}
                disabled={response.response_moderation_status === 'approved'}
              >
                Demander modif.
              </Button>
              <Button
                startIcon={<RejectIcon />}
                variant="outlined"
                color="error"
                size="small"
                onClick={() => openModerationDialog(response, 'reject')}
                disabled={response.response_moderation_status === 'rejected'}
              >
                Rejeter
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Modération des Réponses</Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchResponses}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                label="Statut"
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && !moderationDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : responses.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Aucune réponse à modérer
          </Typography>
        </Paper>
      ) : (
        <>
          {responses.map((response) => (
            <ResponseCard key={response.id} response={response} />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Moderation Dialog */}
      <Dialog
        open={moderationDialogOpen}
        onClose={closeModerationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {ACTION_LABELS[moderationAction]} la réponse
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {selectedResponse && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Réponse concernée :
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedResponse.owner_response}
                </Typography>
              </Paper>
            </Box>
          )}

          <TextField
            label="Notes de modération"
            multiline
            rows={4}
            fullWidth
            value={moderationNotes}
            onChange={handleModerationChange}
            required={moderationAction === 'reject' || moderationAction === 'request_edit'}
            placeholder="Expliquez la raison de votre décision..."
            sx={{ mt: 2 }}
          />

          {/* Bouton de traduction automatique */}
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={translating ? <CircularProgress size={20} /> : <TranslateIcon />}
              onClick={handleTranslateNotes}
              disabled={translating || !moderationNotes || moderationNotes.trim().length < 10}
              variant="outlined"
              size="small"
            >
              {translating ? 'Traduction en cours...' : 'Traduire automatiquement'}
            </Button>
          </Box>

          {/* Zone de traduction */}
          {showTranslation && translationResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="success.dark">
                  ✓ Traduction proposée ({translationResult.targetLanguage})
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={handleUseTranslation}
                >
                  Utiliser cette traduction
                </Button>
              </Stack>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {translatedNotes}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModerationDialog} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleModerationSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting && <CircularProgress size={20} />}
          >
            {submitting ? 'Envoi...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResponseModerationPage;
